if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        // Theme-editor safety, matching the pattern used for Swiper instances
        // elsewhere in this theme (e.g. hero-slideshow.liquid): destroy the
        // mobile zoom/swipe Swiper instance if its section gets removed/
        // re-rendered while the modal (and thus the Swiper) still exists.
        document.addEventListener('shopify:section:unload', (event) => {
          if (this.mobileSwiper && this.id === `ProductModal-${event.detail.sectionId}`) {
            this.mobileSwiper.destroy();
            this.mobileSwiper = null;
          }
        });
      }

      connectedCallback() {
        // IMPORTANT: ModalDialog.connectedCallback() re-parents this element
        // to document.body (a common modal pattern, so it isn't clipped by
        // an ancestor's overflow/stacking context) and guards itself against
        // re-running via `this.moved` -- but connectedCallback necessarily
        // fires AGAIN once that appendChild triggers disconnect+reconnect.
        // Must call super here or the modal loses its body-level positioning
        // entirely.
        super.connectedCallback();

        // Initialize the mobile zoom/swipe Swiper eagerly, at page load,
        // rather than lazily on first tap. Swiper's own script is loaded
        // `defer`, so if init only happened inside show() there was a real
        // race: a fast first swipe right after tapping open could land
        // before Swiper's touch listeners were attached yet (still
        // retry-polling for `window.Swiper` to exist), which is what made
        // swiping feel like it didn't "grab" immediately. Doing this here
        // instead gives it the whole page-load window to be ready well
        // before a user can actually tap to open the modal. Safe to call
        // twice (connectedCallback fires again after the super.appendChild
        // move above) -- ensureMobileSwiper itself guards against starting
        // a second, competing init/retry loop.
        if (this.isMobileZoom()) this.ensureMobileSwiper();
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        if (this.isMobileZoom()) {
          this.showActiveMediaMobile();
        } else {
          this.showActiveMedia();
        }
      }

      // Matches this theme's existing mobile/desktop breakpoint convention
      // (max-width: 749px / min-width: 750px, e.g. assets/media-gallery.js).
      isMobileZoom() {
        return window.matchMedia('(max-width: 749.98px)').matches;
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      // Mobile: bounded pinch/double-tap zoom + swipe between all images via
      // Swiper's Zoom module, instead of native unconstrained pinch-zoom on
      // the whole page. See snippets/product-media-modal.liquid for the
      // swiper/swiper-wrapper/swiper-slide markup this targets.
      //
      // Called from both connectedCallback (eager, no callback) and
      // showActiveMediaMobile (on tap, with a callback) -- can't just check
      // `if (this.mobileSwiper)` to decide whether to start a retry loop,
      // since a second call can arrive while the FIRST is still mid-retry
      // waiting on window.Swiper (e.g. connectedCallback's double-fire, or a
      // very fast tap right after page load). Queuing callbacks and only
      // ever running one retry loop (`mobileSwiperInitStarted`) avoids ever
      // creating two competing Swiper instances on the same element.
      ensureMobileSwiper(onReady) {
        const swiperEl = this.querySelector('.product-media-modal__swiper');
        if (!swiperEl) return;

        if (this.mobileSwiper) {
          if (onReady) onReady(this.mobileSwiper);
          return;
        }

        if (!this.mobileSwiperReadyQueue) this.mobileSwiperReadyQueue = [];
        if (onReady) this.mobileSwiperReadyQueue.push(onReady);

        if (this.mobileSwiperInitStarted) return;
        this.mobileSwiperInitStarted = true;

        const init = () => {
          if (typeof Swiper === 'undefined') {
            setTimeout(init, 50);
            return;
          }
          this.mobileSwiper = new Swiper(swiperEl, {
            zoom: {
              maxRatio: 3,
              minRatio: 1,
            },
          });
          this.mobileSwiperReadyQueue.forEach((callback) => callback(this.mobileSwiper));
          this.mobileSwiperReadyQueue = [];
        };

        init();
      }

      showActiveMediaMobile() {
        const openedMediaId = this.openedBy.getAttribute('data-media-id');

        this.ensureMobileSwiper((swiper) => {
          const slides = swiper.slides;
          const startIndex = Math.max(
            0,
            Array.from(slides).findIndex((slide) => slide.querySelector(`[data-media-id="${openedMediaId}"]`))
          );

          // The container was hidden (visibility:hidden) since page load,
          // possibly since before this Swiper instance was even created --
          // force a re-measure now that the modal is actually visible, in
          // case anything (viewport rotation, variant-driven slide changes)
          // shifted dimensions since then.
          swiper.update();
          swiper.slideTo(startIndex, 0);

          this.loadDeferredMediaContent(slides[startIndex]);
        });
      }

      // Same deferred-youtube-content loading showActiveMedia() does for
      // desktop, applied to whichever slide the mobile swiper opened on.
      loadDeferredMediaContent(slide) {
        const activeMedia = slide && slide.querySelector('[data-media-id]');
        if (!activeMedia) return;
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
